/** @format */
/** istanbul ignore file */

import { ActivatedRoute, Data, NavigationEnd, Params, Router, RouterModule } from '@angular/router';
import {
	CartService,
	CookieService,
	HeaderLocationService,
	KillswitchService,
	Store,
	StoreService,
	UtilityService,
	VirtualHostCookieService,
	WINDOW
} from '@shared/data-access/services';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	effect,
	HostListener,
	inject,
	OnInit,
	PLATFORM_ID,
	TemplateRef,
	viewChild
} from '@angular/core';
import { CompareProductDetails } from '@compare/data-access';
import { ComparePaintedDoorModalComponent, ProductListSnackbarComponent } from '@plp/components';
import {
	CompareBarProduct,
	Customer,
	ProductAvailabilityCollection
} from '@shared/data-access/interfaces';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { firstValueFrom, Observable, of } from 'rxjs';
import { ProductList, ProductListCore } from '@plp/data-access/interfaces';
import {
	ProductListCompareService,
	ProductListMlService,
	ProductSelectionStateService
} from '@plp/data-access/services';
import {
	changeHeaderServiceStoreInfo,
	setCommonData,
	setCommonDsgVhHeader,
	setUserStoreInfoFromCookie,
	setUserStoreName,
	setUserZipCode
} from '@state/data-access';
import { filter, map, mergeMap, take } from 'rxjs/operators';
import {
	refreshProductList,
	selectCompareBarProducts,
	selectCompareProductDetails,
	selectCompareProductDetailsFailed,
	selectCompareProductsAvailability,
	selectCompareSkeletonLoader,
	selectFeatureFlags,
	selectIsCompareGridOpen,
	selectProductListCore
} from '@plp/data-access/state';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import { AuthenticationService } from '@shared/authentication/data-access';
import { CeopsComponent } from '@shared/ceops';
import { CompareGridComponent } from '@compare/components';
import { MetaService } from '@seo/data-access';
import { Store as NgRxStore } from '@ngrx/store';
import { ProductAnalyticsService } from '@shared/data-access/analytics';
import { analyticsSortMethodValues } from '@plp/data-access/constants';
import { chain } from '@core/chain';

declare const BUILD_VERSION: string;

const IMPORTS = [
	CeopsComponent,
	ComparePaintedDoorModalComponent,
	CompareGridComponent,
	ProductListSnackbarComponent,
	RouterModule
];
@Component({
	selector: 'plp-srlp-root',
	templateUrl: './app.component.html',
	styleUrl: './app.component.scss',
	imports: [...IMPORTS],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit {
	private readonly activatedRoute = inject(ActivatedRoute);
	private readonly analyticsService = inject(ProductAnalyticsService);
	private readonly authService = inject(AuthenticationService);
	private readonly cartService = inject(CartService);
	private readonly cookieService = inject(CookieService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly document = inject(DOCUMENT);
	private readonly headerLocationService = inject(HeaderLocationService);
	private readonly killSwitchService = inject(KillswitchService);
	private readonly metaService = inject(MetaService);
	private readonly ngrxStore = inject(NgRxStore);
	private readonly plpMlService = inject(ProductListMlService);
	private readonly productSelectionStateService = inject(ProductSelectionStateService);
	private readonly router = inject(Router);
	private readonly store = inject(Store);
	private readonly storeService = inject(StoreService);
	private readonly utilityService = inject(UtilityService);
	private readonly virtualHostCookieService = inject(VirtualHostCookieService);
	private readonly window = inject<Window>(WINDOW);
	protected readonly plpCompareService = inject(ProductListCompareService);

	platformId = inject(PLATFORM_ID);

	/**
	 * @description
	 * The Customer object from application state
	 */
	private readonly customer$ = this.store
		.select<Customer>('CUSTOMER')
		.pipe(takeUntilDestroyed(this.destroyRef));
	readonly featureFlags = this.ngrxStore.selectSignal(selectFeatureFlags);
	readonly compareGridEnabled = computed(() => this.featureFlags()?.compareGridEnabled);
	chainIdentifierAbbr = chain.chainIdentifierAbbr;
	chainMarketingIdentifierAbbr = chain.chainMarketingIdentifierAbbr;
	feedbackSurveyId = chain.feedbackSurveyId;
	/**
	 * @description
	 * launch flag used to hide/show things in ticker and footer
	 */
	isSecureLaunch = false;

	/**
	 * @description
	 * Used to determine whether or not the ticker name has been set to the product ID
	 */
	isTickerPageNameSet = false;
	ranAlready = false;
	sddZipCode = '';
	compareCheckboxEnabled = false;
	readonly modalContent = viewChild.required<TemplateRef<unknown>>('modalContent');
	readonly isCompareGridOpen = this.ngrxStore.selectSignal(selectIsCompareGridOpen);
	readonly compareProductDetails = this.ngrxStore.selectSignal<CompareProductDetails>(
		selectCompareProductDetails
	);
	readonly compareBarProducts =
		this.ngrxStore.selectSignal<CompareBarProduct[]>(selectCompareBarProducts);
	readonly compareProductsAvailability =
		this.ngrxStore.selectSignal<ProductAvailabilityCollection>(
			selectCompareProductsAvailability
		);
	readonly isProductDataLoading = this.ngrxStore.selectSignal<boolean>(
		selectCompareSkeletonLoader
	);
	readonly hasApiFailed = this.ngrxStore.selectSignal<boolean>(selectCompareProductDetailsFailed);

	private readonly environmentKillSwitchValues = toSignal(
		this.killSwitchService.getKillSwitchesEnvironment$()
	);

	private readonly currentRouteSignal = toSignal(
		this.router.events.pipe(
			filter((event) => event instanceof NavigationEnd),
			map(() => this.activatedRoute),
			map((route) => {
				while (route.firstChild) {
					route = route.firstChild;
				}
				return route;
			}),
			mergeMap((route: ActivatedRoute) =>
				of({
					data: route.data,
					params: route.params,
					queryParams: route.queryParams
				})
			)
		)
	);

	constructor() {
		// Check authentication status and set up authentication status stream
		this.authService.localAuthSetup();

		// set dsg_vh in the common state
		this.ngrxStore.dispatch(setCommonData());
		this.ngrxStore.dispatch(setCommonDsgVhHeader({ dsgVh: this.cookieService.get('DSG_VH') }));

		// Reset the analytics
		if (!this.window.siteData) {
			this.analyticsService.resetAnalytics();
		}

		// Check to see if this has been defined
		if (typeof BUILD_VERSION === 'string' && BUILD_VERSION.trim() !== '') {
			// Set the build version on the global window
			this.window.BUILD_VERSION = BUILD_VERSION;
		}

		/*
		 * Only execute on browser
		 */
		if (isPlatformBrowser(this.platformId)) {
			// eliminate 'persistent' click focus
			document.body.addEventListener('mousedown', () => {
				document.body.classList.add('mouse-Event');
			});

			document.body.addEventListener('keydown', () => {
				document.body.classList.remove('mouse-Event');
			});

			// Initialize the Cart Service
			this.cartService.initialize();

			// Check cookie and populate Store
			this.authService.checkIfReturningCustomer();

			// Set default store
			this.storeService.initialize();

			/*
			 * DSG_UID cookie
			 */
			if (!this.cookieService.check('DSG_UID')) {
				// Create a DSG_UID cookie
				this.cookieService.set(
					'DSG_UID',
					this.utilityService.generateUUID(),
					365,
					'/',
					'',
					''
				);
			}

			// Virtual Host chain identifier
			this.virtualHostCookieService.setVirtualHostCookie();
		}

		effect(() => {
			const currentRouteSignal = this.currentRouteSignal();
			if (currentRouteSignal) {
				this.routerSubscriptionHandler(currentRouteSignal);
			}
		});

		effect(() => {
			this.store.set('ENVIRONMENT_KILLSWITCH_VALUES', this.environmentKillSwitchValues());
		});

		effect(() => {
			if (this.compareGridEnabled()) {
				this.plpCompareService.initCompareGrid(
					this.modalContent(),
					'plp-compare-drawer-wrapper'
				);
			}
		});
	}

	ngOnInit(): void {
		this.setStoreInfoZipCodeToStore();
	}

	private setCustomerOptions(customer: Customer): void {
		const { x_acNumber = '', x_acBalancePoints = '', sessionId, userId, visitCount } = customer;

		// Set the Loyalty ID
		this.analyticsService.setLoyaltyID(x_acNumber);

		// Set the Loyalty Balance
		this.analyticsService.setLoyaltyPointsBalance(x_acBalancePoints);

		// Set the session ID
		this.analyticsService.setSessionID(sessionId);

		// Set the user ID
		this.analyticsService.setUserID(userId);

		// Set the visits count
		this.analyticsService.setVisitNumber(visitCount);
	}

	private triggerAnalytics(CURRENT_QUERY_PARAMS) {
		// Get the current Date/Time
		const CURRENT_DATETIME = new Date();

		// Set the chain
		this.analyticsService.setChain(chain.chainName);

		// Set the Error ID (if present)
		this.analyticsService.setErrorId();

		// Set the Error Message (if present)
		this.analyticsService.setErrorMessage();

		// Set the referring URL
		this.analyticsService.setReferringPageUrl(this.document.referrer);

		// Set the server date
		this.analyticsService.setServerDate(
			this.analyticsService.getFormattedDate(CURRENT_DATETIME)
		);

		// Set the server time
		this.analyticsService.setServerTime(
			this.analyticsService.getFormattedTime(CURRENT_DATETIME)
		);

		// Set the base Search properties
		this.analyticsService.setSearchPageBaseProperties({
			searchDetails: {
				DYMTerm: CURRENT_QUERY_PARAMS.dymterm ?? CURRENT_QUERY_PARAMS.DYMTerm ?? '',
				DYMType: CURRENT_QUERY_PARAMS.didyoumean ?? CURRENT_QUERY_PARAMS.didYouMean ?? '',
				replacementTerm: CURRENT_QUERY_PARAMS.replacementTerm ?? '',
				searchTerm:
					CURRENT_QUERY_PARAMS.enteredSearchTerm ?? CURRENT_QUERY_PARAMS.searchTerm ?? '',
				searchType: CURRENT_QUERY_PARAMS.DSGsearchType ?? ''
			}
		});

		if (!this.ranAlready) {
			const isFamilyPage = this.productSelectionStateService.getPageTypeData() === 'plp';
			const pageType = isFamilyPage ? 'Family' : 'Search';
			this.analyticsService.setType('page', pageType);
			this.setFamilySRLPAnalyticsData();
			this.ranAlready = true;
		}

		// Get the Customer from state and populate related analytics

		this.customer$.subscribe((customer: Customer) => {
			this.setCustomerOptions(customer);
		});

		// Fallback in case of 404 so that the default ticker is rendered
		this.isTickerPageNameSet = true;

		// Check to see if the `param` query parameter is present
		if (
			typeof CURRENT_QUERY_PARAMS.param === 'string' &&
			CURRENT_QUERY_PARAMS.param.trim() !== '' &&
			!this.cookieService.check('promoId')
		) {
			// Set the promoId cookie
			this.cookieService.set('promoId', CURRENT_QUERY_PARAMS.param, '', '/', '', '');
		}
	}

	private routerSubscriptionHandler(route: {
		data: Observable<Data>;
		params: Observable<Params>;
		queryParams: Observable<Params>;
	}): void {
		// Get a Promise for route.data
		const dataPromise = firstValueFrom(route.data.pipe(take(1)));

		// Get a Promise for route.params
		const paramsPromise = firstValueFrom(route.params.pipe(take(1)));

		// Get a Promise for route.queryParams
		const queryParamsPromise = firstValueFrom(route.queryParams.pipe(take(1)));

		// Wait until all of the Promises have been resolved
		Promise.all([dataPromise, paramsPromise, queryParamsPromise]).then((data) => {
			// Get the current query parameters from the route query params
			const CURRENT_QUERY_PARAMS = data[2];

			// Only execute in the Browser
			if (isPlatformBrowser(this.platformId)) {
				this.utilityService.deferOnPrerendering(() =>
					this.triggerAnalytics(CURRENT_QUERY_PARAMS)
				);
				// Only execute on the Server
			} else {
				/*
				 * Canonical link element
				 */
				// Canonical URL
				const canonicalUrl = `${chain.metaCanonical}${this.router.url.split('?')[0]}`;

				this.createCanonicalTag(canonicalUrl);

				/*
				 * OpenGraph url meta tag
				 */
				this.metaService.addTag({ property: 'og:url', content: canonicalUrl }, true);
			}
		});
	}

	private setStoreInfoFromCookieString(cookieString: string): void {
		const dataFromStoreCookieString =
			this.storeService.getStoreDataFromStoreCookieString(cookieString);

		if (dataFromStoreCookieString?.storeZip || dataFromStoreCookieString?.storeNumber) {
			this.ngrxStore.dispatch(
				setUserStoreInfoFromCookie({
					zipcode: dataFromStoreCookieString.storeZip ?? '',
					location: dataFromStoreCookieString.storeNumber ?? '',
					name: dataFromStoreCookieString.storeName ?? ''
				})
			);
		}
	}

	private setUserZipCodeFromCookie(): void {
		if (!this.sddZipCode) {
			this.sddZipCode =
				this.headerLocationService.getDeliveryZipCookie() ||
				this.headerLocationService.getWhereaboutsCookie();

			if (this.sddZipCode) {
				this.ngrxStore.dispatch(setUserZipCode({ zipCode: this.sddZipCode }));
			}
		}
	}

	private setStoreInfoZipCodeToStore(): void {
		if (isPlatformBrowser(this.platformId)) {
			// Set the user zip code from the cookie
			this.setUserZipCodeFromCookie();

			/** Get the store cookie string and set the store info
			 */
			const storeCookieString = this.storeService.getSavedCustomerStore();
			if (storeCookieString) {
				this.setStoreInfoFromCookieString(storeCookieString);
			}
		}
	}

	createCanonicalTag(canonicalUrl: string): void {
		// Check to see canonical is already set for plp. To avoid duplicate canonical tag.
		if (
			this.productSelectionStateService.getPageTypeData() === 'plp' &&
			this.document.head.querySelector('[rel="canonical"]')
		) {
			return;
		}

		// Create a link element
		const canonicalLink = this.document.createElement('link');

		// Set the link relationship attribute
		canonicalLink.setAttribute('rel', 'canonical');

		// Set the link href attribute
		canonicalLink.setAttribute('href', canonicalUrl);

		// Append the link element to the head element
		this.document.head.appendChild(canonicalLink);
	}

	private setFamilySRLPAnalyticsData() {
		this.ngrxStore
			.select(selectProductListCore)
			.pipe(take(1))
			.subscribe((plcData: ProductListCore) => {
				const mlSearchEventId = this.plpMlService.getMlSearchEventParentUUID();
				const productList = plcData?.productList;
				const isSearchRedirect = productList?.searchVO?.landingPageURL;

				/**
					If search page redirect to any other page, do not send reporting:PagePostRender.
				*/
				if (isSearchRedirect) {
					return;
				}

				if (productList?.didYouMeanVO) {
					this.window.siteData.DYMTerm = productList?.didYouMeanVO?.suggestions[0];
					this.window.siteData.DYMType = 'DYMAuto';
					this.window.siteData.EnteredSearchTerm =
						productList?.didYouMeanVO?.originalSearchTerm;
				}

				this.window.siteData.FamilyID = plcData.productListSEO?.id;
				this.window.siteData.FamilyName = plcData.productListSEO?.name;
				this.window.siteData.FuzzyMatch = productList?.fuzzyMatch;
				this.window.siteData.LoggedIn = undefined;
				this.window.siteData.MCSalesCatalogDetails = [
					{ code: plcData.productListSEO?.identifier }
				];
				this.window.siteData.MLBypass = !!productList?.mlBypass;
				this.window.siteData.MLGroup = productList?.mlGroup;
				this.window.siteData.MLRank = productList?.mlRank ?? '';
				this.window.siteData.MLRewrite = productList?.mlRewrite ?? '';
				this.window.siteData.MLSpelling = !!productList?.mlSpelling;
				this.window.siteData.PLP_event_id = mlSearchEventId;
				this.window.siteData.ProductCountTotal = productList?.totalCount;
				this.window.siteData.ProductPerPage = productList?.searchVO.pageSize;
				this.window.siteData.ProductsInFamily = productList?.totalCount;
				this.window.siteData.SearchData = productList?.searchVO.searchTypes;
				this.window.siteData.SearchResultCount =
					productList?.searchVO.totalCount.toString();
				this.window.siteData.SearchType = this.setSearchType(productList);
				this.window.siteData.SortMethod = analyticsSortMethodValues.find(
					(dropdownOption) => Number(plcData.selectedSort.value) === dropdownOption.value
				)?.text;
				this.window.siteData.ReviewStarsProducts = productList?.productVOs?.filter(
					(product) => product.ratingCount > 0
				).length;

				this.window.siteData.ShipCount =
					productList?.fulfillmentFacets?.find((f) => f.name === 'SHIP')?.count || 0;
				this.window.siteData.PickupCount =
					productList?.fulfillmentFacets?.find((f) => f.name === 'BOPIS')?.count || 0;
				this.window.siteData.SameDayCount =
					productList?.fulfillmentFacets?.find((f) => f.name === 'SDD')?.count || 0;

				this.analyticsService.triggerAnalyticsEvent('reporting:PagePostRender');
			});
	}

	setSearchType(productList: ProductList): string {
		const searchTypeDefault = 'Keyword';
		const isFamilyPage = this.productSelectionStateService.getPageTypeData() === 'plp';
		const searchRefinement = this.cookieService.get('searchRefinement');

		if (productList?.totalCount === 0) {
			return '';
		}

		if (isFamilyPage) {
			const sTermCookie = this.cookieService.get('sTermCookie');
			if (sTermCookie) {
				this.window.siteData.EnteredSearchTerm = sTermCookie;
				this.cookieService.delete('sTermCookie', '/', '');
				return 'Redirect';
			}

			if (searchRefinement === 'categories') {
				return 'Suggested Category';
			} else {
				return '';
			}
		}

		if (!productList?.searchVO.landingPageURL && this.cookieService.get('sTermCookie')) {
			this.cookieService.delete('sTermCookie', '/', '');
		}

		//srlp
		if (searchRefinement && searchRefinement.indexOf('Recent') > -1) {
			return 'Suggested History';
		}

		switch (searchRefinement) {
			case 'brands': {
				return 'Suggested Brand';
			}
			case 'enterSearchTerm': {
				return 'Keyword';
			}
			case 'searchTerm': {
				return 'Suggested Keyword';
			}
			case 'recentlySearchedTerm': {
				return 'Suggested Recently Searched';
			}
			default:
				return searchTypeDefault;
		}
	}

	/**
	 * @description use the header service to get athlete's delivery zip code on page load
	 */
	@HostListener('window:HeaderServices:SameDayDelivery:OnLoad', ['$event'])
	setDeliveryZipOnLoad(event) {
		if (event?.detail && event.detail !== this.sddZipCode) {
			this.sddZipCode = event.detail;
			this.ngrxStore.dispatch(setUserZipCode({ zipCode: event.detail }));
		}
	}

	/**
	 * @description use the header service to get athlete's delivery zip code on change
	 */
	@HostListener('window:HeaderServices:SameDayDelivery:Change', ['$event'])
	setDeliveryZipOnChange(event) {
		if (event?.detail && event.detail !== this.sddZipCode) {
			this.sddZipCode = event.detail;
			this.ngrxStore.dispatch(setUserZipCode({ zipCode: event.detail }));
			this.ngrxStore.dispatch(refreshProductList());
		}
	}

	/**
	 * @description Listen for any changes in the header service to the store selection
	 * if there is a change update the state
	 */
	@HostListener('window:HeaderServices:StoreSelection:Change', ['$event'])
	updateStoreDetails(event) {
		if (event?.detail) {
			this.ngrxStore.dispatch(changeHeaderServiceStoreInfo({ storeInfo: event.detail }));
			this.ngrxStore.dispatch(refreshProductList());

			if (event.detail?.name) {
				this.ngrxStore.dispatch(setUserStoreName({ storeName: event.detail.name }));
			}
		}
	}
}
